import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { useOrders } from "@/hooks/use-orders";
import { useStores } from "@/hooks/use-stores";
import { formatCurrency, formatDate } from "@/lib/utils";
import type { Order, OrderItem } from "@/types";
import { ExternalLink, Eye } from "lucide-react";
import { useState } from "react";

const statusVariant: Record<string, "success" | "warning" | "destructive" | "secondary"> = {
	placed: "success",
	draft: "warning",
	failed: "destructive",
	cancelled: "secondary",
};

const confidenceVariant: Record<string, "success" | "warning" | "destructive"> = {
	high: "success",
	medium: "warning",
	low: "destructive",
};

export function OrdersPage() {
	const [storeFilter, setStoreFilter] = useState<string | undefined>();
	const [statusFilter, setStatusFilter] = useState<string | undefined>();
	const [page, setPage] = useState(1);
	const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);

	const { stores } = useStores();
	const { orders, total, loading } = useOrders({
		storeId: storeFilter,
		status: statusFilter,
		page,
	});

	const totalPages = Math.ceil(total / 25);

	return (
		<div className="space-y-8">
			<div>
				<h1 className="text-3xl font-bold tracking-tight">Orders</h1>
				<p className="text-muted-foreground">
					Review orders automatically converted from customer emails
				</p>
			</div>

			{/* Filters */}
			<div className="flex flex-wrap gap-3">
				<select
					className="rounded-md border border-input bg-background px-3 py-2 text-sm"
					value={storeFilter ?? ""}
					onChange={(e) => {
						setStoreFilter(e.target.value || undefined);
						setPage(1);
					}}
				>
					<option value="">All Stores</option>
					{stores.map((s) => (
						<option key={s.id} value={s.id}>
							{s.name}
						</option>
					))}
				</select>
				<select
					className="rounded-md border border-input bg-background px-3 py-2 text-sm"
					value={statusFilter ?? ""}
					onChange={(e) => {
						setStatusFilter(e.target.value || undefined);
						setPage(1);
					}}
				>
					<option value="">All Statuses</option>
					<option value="placed">Placed</option>
					<option value="draft">Draft</option>
					<option value="failed">Failed</option>
					<option value="cancelled">Cancelled</option>
				</select>
			</div>

			{/* Orders Table */}
			<Card>
				<CardContent className="p-0">
					{loading ? (
						<div className="flex items-center justify-center py-12 text-muted-foreground">
							Loading orders...
						</div>
					) : orders.length === 0 ? (
						<div className="flex flex-col items-center justify-center py-12">
							<p className="text-muted-foreground">No orders found</p>
						</div>
					) : (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Date</TableHead>
									<TableHead>Store</TableHead>
									<TableHead>Customer</TableHead>
									<TableHead>Items</TableHead>
									<TableHead>Total</TableHead>
									<TableHead>Status</TableHead>
									<TableHead>Confidence</TableHead>
									<TableHead />
								</TableRow>
							</TableHeader>
							<TableBody>
								{orders.map((order) => (
									<TableRow key={order.id}>
										<TableCell className="text-muted-foreground whitespace-nowrap">
											{formatDate(order.created_at)}
										</TableCell>
										<TableCell>
											{(order as Order & { store?: { name: string } }).store?.name ?? "—"}
										</TableCell>
										<TableCell>
											<div>
												<p className="font-medium">{order.customer_name ?? "Unknown"}</p>
												<p className="text-xs text-muted-foreground">{order.customer_email}</p>
											</div>
										</TableCell>
										<TableCell>
											{order.items?.length ?? 0} item
											{(order.items?.length ?? 0) !== 1 ? "s" : ""}
										</TableCell>
										<TableCell>
											{order.total_amount != null ? formatCurrency(order.total_amount) : "—"}
										</TableCell>
										<TableCell>
											<Badge variant={statusVariant[order.status] ?? "secondary"}>
												{order.status}
											</Badge>
										</TableCell>
										<TableCell>
											<Badge variant={confidenceVariant[order.confidence] ?? "secondary"}>
												{order.confidence}
											</Badge>
										</TableCell>
										<TableCell>
											<div className="flex gap-1">
												<Button
													variant="ghost"
													size="icon"
													className="h-8 w-8"
													onClick={() => setSelectedOrder(order)}
												>
													<Eye className="h-4 w-4" />
												</Button>
												{order.shopify_order_id && (
													<a
														href={`https://${(order as Order & { store?: { shopify_domain: string } }).store?.shopify_domain}/admin/orders/${order.shopify_order_id}`}
														target="_blank"
														rel="noopener noreferrer"
													>
														<Button variant="ghost" size="icon" className="h-8 w-8">
															<ExternalLink className="h-4 w-4" />
														</Button>
													</a>
												)}
											</div>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					)}
				</CardContent>
			</Card>

			{/* Pagination */}
			{totalPages > 1 && (
				<div className="flex items-center justify-center gap-2">
					<Button
						variant="outline"
						size="sm"
						disabled={page <= 1}
						onClick={() => setPage((p) => p - 1)}
					>
						Previous
					</Button>
					<span className="text-sm text-muted-foreground">
						Page {page} of {totalPages}
					</span>
					<Button
						variant="outline"
						size="sm"
						disabled={page >= totalPages}
						onClick={() => setPage((p) => p + 1)}
					>
						Next
					</Button>
				</div>
			)}

			{/* Order Detail Modal */}
			{selectedOrder && (
				<OrderDetailModal order={selectedOrder} onClose={() => setSelectedOrder(null)} />
			)}
		</div>
	);
}

// ── Order Detail Modal ───────────────────────────────────────────────────────

function OrderDetailModal({
	order,
	onClose,
}: {
	order: Order;
	onClose: () => void;
}) {
	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
			<Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
				<CardHeader>
					<div className="flex items-center justify-between">
						<div>
							<CardTitle>Order Details</CardTitle>
							<CardDescription>
								{order.shopify_order_number
									? `Shopify #${order.shopify_order_number}`
									: `Internal ${order.id.slice(0, 8)}`}
							</CardDescription>
						</div>
						<div className="flex gap-2">
							<Badge variant={statusVariant[order.status] ?? "secondary"}>{order.status}</Badge>
							<Badge variant={confidenceVariant[order.confidence] ?? "secondary"}>
								{order.confidence} confidence
							</Badge>
						</div>
					</div>
				</CardHeader>
				<CardContent className="space-y-6">
					{/* Customer Info */}
					<div>
						<h3 className="text-sm font-semibold mb-2">Customer</h3>
						<div className="text-sm space-y-1">
							<p>{order.customer_name ?? "Unknown"}</p>
							<p className="text-muted-foreground">{order.customer_email}</p>
							{order.customer_phone && (
								<p className="text-muted-foreground">{order.customer_phone}</p>
							)}
						</div>
					</div>

					{/* Items */}
					<div>
						<h3 className="text-sm font-semibold mb-2">Items</h3>
						<div className="space-y-2">
							{order.items?.map((item: OrderItem, i: number) => (
								<div
									key={`${item.product_name}-${i}`}
									className="flex items-center justify-between rounded-md border p-3"
								>
									<div>
										<p className="font-medium">{item.product_name}</p>
										{item.variant_title && (
											<p className="text-xs text-muted-foreground">{item.variant_title}</p>
										)}
									</div>
									<div className="text-right">
										<p>x{item.quantity}</p>
										{item.unit_price != null && (
											<p className="text-sm text-muted-foreground">
												{formatCurrency(item.unit_price)} each
											</p>
										)}
									</div>
								</div>
							))}
						</div>
					</div>

					{/* Shipping Address */}
					{order.shipping_address && (
						<div>
							<h3 className="text-sm font-semibold mb-2">Shipping Address</h3>
							<div className="text-sm text-muted-foreground">
								<p>{order.shipping_address.address1}</p>
								{order.shipping_address.address2 && <p>{order.shipping_address.address2}</p>}
								<p>
									{order.shipping_address.city}, {order.shipping_address.province}{" "}
									{order.shipping_address.zip}
								</p>
								<p>{order.shipping_address.country}</p>
							</div>
						</div>
					)}

					{/* Notes / Error */}
					{order.notes && (
						<div>
							<h3 className="text-sm font-semibold mb-2">Notes</h3>
							<p className="text-sm text-muted-foreground">{order.notes}</p>
						</div>
					)}
					{order.error_message && (
						<div>
							<h3 className="text-sm font-semibold mb-2 text-destructive">Error</h3>
							<p className="text-sm text-destructive">{order.error_message}</p>
						</div>
					)}

					{/* Total */}
					{order.total_amount != null && (
						<div className="flex justify-between border-t pt-4">
							<span className="font-semibold">Total</span>
							<span className="font-semibold">{formatCurrency(order.total_amount)}</span>
						</div>
					)}
				</CardContent>
				<div className="p-6 pt-0">
					<Button variant="outline" className="w-full" onClick={onClose}>
						Close
					</Button>
				</div>
			</Card>
		</div>
	);
}
